


// Foodreports.js
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    BarChart2, TrendingUp, ShoppingCart, CreditCard, IndianRupee,
    Star, FileText, Store, Menu, X, Utensils, ShoppingBag,
    Users, Pizza, Cake, Coffee, Calendar as CalendarIcon,
    RotateCcw, Download, Clock, AlertCircle
} from "lucide-react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from "recharts";
import * as XLSX from 'xlsx';
import "./ReportAnalysisTab.css";

// Import sub-components
import Cateringrevenue from "./Cateringrevenue";
import Cateringratings from "./Cateringratings";
import Cateringfinancials from "./Cateringfinancials";
import Cateringsorders from "./Cateringsorders";
import Cateringpayments from "./Cateringpayments";

// Define all available tabs with their submodule mapping
const ALL_TABS = [
    { id: "Financials", label: "Financials", icon: BarChart2, subModuleKey: "REPORTS_OVERVIEW" },
    { id: "Revenue", label: "Revenue", icon: TrendingUp, subModuleKey: "REPORTS_REVENUE" },
    { id: "orders", label: "Orders", icon: ShoppingCart, subModuleKey: "REPORTS_ORDERS" },
    { id: "Payments", label: "Payments", icon: CreditCard, subModuleKey: "REVIEWS_RATINGS" },
];

// Inline loader hook
const useInlineLoader = () => {
    const [loading, setLoading] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);

    const showLoader = () => {
        setFadeOut(false);
        setLoading(true);
    };

    const hideLoader = () => {
        setFadeOut(true);
        setTimeout(() => {
            setLoading(false);
            setFadeOut(false);
        }, 500);
    };

    return { showLoader, hideLoader, loading, fadeOut };
};

const Foodreports = () => {
    const [activeTab, setActiveTab] = useState(null);
    const [tabs, setTabs] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [vendorId, setVendorId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [timeRange, setTimeRange] = useState('monthly');
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [activeView, setActiveView] = useState('revenue');
    const [resetting, setResetting] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [timeFilter, setTimeFilter] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [customTotals, setCustomTotals] = useState(null);
    const [appliedStartDate, setAppliedStartDate] = useState(null);
    const [appliedEndDate, setAppliedEndDate] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [apiStats, setApiStats] = useState(null);
    const [dateRangeData, setDateRangeData] = useState([]);
    const [isCustomRangeActive, setIsCustomRangeActive] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [filteredData, setFilteredData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Use inline loader
    const { showLoader, hideLoader, loading: globalLoading, fadeOut } = useInlineLoader();

    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split("T")[0];
    });

    const [endDate, setEndDate] = useState(() => {
        return new Date().toISOString().split("T")[0];
    });

    // Colors
    const colors = {
        primary: '#7c3aed',
        secondary: '#10b981',
        accent: '#f59e0b',
        danger: '#ef4444',
        info: '#3b82f6',
        success: '#10b981',
        warning: '#f59e0b',
        dark: '#1f2937',
        light: '#f9fafb',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    };

    // Fetch Admin Statistics API
    const fetchAdminStatistics = async () => {
        setLoading(true);
        showLoader();
        try {
            const token = localStorage.getItem("authToken");
            if (!token) {
                console.error("No auth token found. Please login.");
                setLoading(false);
                hideLoader();
                return;
            }

            const response = await axios.get(
                "https://backend.maamaas.com/catering/api/admin/orders/statistics/dashboard",
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );

            //console.log("📊 Dashboard API Response:", response.data);
            setApiStats(response.data);
            setApiError(null);

        } catch (err) {
            console.error("❌ Failed to fetch statistics:", err);
            setApiError("Failed to load dashboard data");
            setApiStats({
                totalRevenue: 0,
                totalOrders: 0,
                totalCustomers: 0,
                successRate: "0%",
                completedOrders: 0,
                cancelledOrders: 0,
                refundedOrders: 0
            });
        } finally {
            setLoading(false);
            hideLoader();
        }
    };

    // Fetch custom range data
    const fetchCustomRangeTotals = async (fromDate, toDate) => {
        if (!fromDate || !toDate) {
            //console.log("Missing dates:", { fromDate, toDate });
            return;
        }

        setLoadingStats(true);
        showLoader();
        setIsCustomRangeActive(true);
        setApiError(null);

        try {
            const token = localStorage.getItem("authToken");
            if (!token) {
                throw new Error("No auth token found. Please login.");
            }

            const response = await axios.get(
                `https://backend.maamaas.com/catering/api/admin/orders/statistics/dashboard?fromDate=${fromDate}&toDate=${toDate}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );

            const apiData = response.data || {};
            //console.log("✅ Custom Range API Response:", apiData);

            const mappedTotals = {
                revenue: Number(apiData.totalRevenue || 0),
                orders: Number(apiData.totalOrders || 0),
                successRate: apiData.successRate || "0%",
                customers: Number(apiData.totalCustomers || 0),
                completedOrders: Number(apiData.completedOrders || 0),
                cancelledOrders: Number(apiData.cancelledOrders || 0),
                refundedOrders: Number(apiData.refundedOrders || 0),
                totalVendors: Number(apiData.totalVendors || 0),
                activeVendors: Number(apiData.activeVendors || 0),
                period: apiData.period || "Custom",
                _rawApiData: apiData,
                _requestedDates: { fromDate, toDate },
                _source: 'actual-api'
            };

            setCustomTotals(mappedTotals);
            setTimeRange("custom");
            generateDateRangeData(fromDate, toDate, mappedTotals);

        } catch (err) {
            console.error("❌ Error fetching custom range:", err);
            const daysDiff = getDaysDifference(fromDate, toDate);
            const estimatedTotals = {
                revenue: Math.round((10657 / 25) * daysDiff * 100) / 100,
                orders: Math.max(1, Math.round((8 / 25) * daysDiff)),
                successRate: "0.00%",
                customers: Math.max(1, Math.round((2 / 25) * daysDiff)),
                completedOrders: 0,
                cancelledOrders: 0,
                refundedOrders: 0,
                period: "Today",
                _error: "API error - using estimation",
                _source: 'estimated'
            };

            setApiError("Using estimated data - API connection issue");
            setCustomTotals(estimatedTotals);
            setTimeRange("custom");
            generateDateRangeData(fromDate, toDate, estimatedTotals);

        } finally {
            setLoadingStats(false);
            hideLoader();
        }
    };

    // Generate daily data for date range
    const generateDateRangeData = (fromDate, toDate, totals) => {
        const daysDiff = getDaysDifference(fromDate, toDate);
        const data = [];
        const start = new Date(fromDate);
        const totalRevenue = totals.revenue || 0;
        const totalOrders = totals.orders || 0;

        let remainingRevenue = totalRevenue;
        let remainingOrders = totalOrders;

        for (let i = 0; i < daysDiff; i++) {
            const currentDate = new Date(start);
            currentDate.setDate(start.getDate() + i);

            if (i === daysDiff - 1) {
                const dailyOrders = Math.max(0, remainingOrders);
                const dailyRevenue = Math.max(0, Math.round(remainingRevenue * 100) / 100);
                const completed = Math.round(dailyOrders * 0.85);
                const cancelled = Math.round(dailyOrders * 0.08);

                data.push({
                    date: currentDate.toISOString().split('T')[0],
                    label: currentDate.toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short'
                    }),
                    orders: dailyOrders,
                    revenue: dailyRevenue,
                    completed: completed,
                    cancelled: cancelled,
                    inProgress: Math.max(0, dailyOrders - completed - cancelled)
                });
            } else {
                const variation = 0.7 + (Math.random() * 0.6);
                const dailyOrders = Math.max(0, Math.round((totalOrders / daysDiff) * variation));
                const dailyRevenue = Math.max(0, Math.round((totalRevenue / daysDiff) * variation * 100) / 100);
                const actualOrders = Math.min(dailyOrders, remainingOrders);
                const actualRevenue = Math.min(dailyRevenue, remainingRevenue);
                const completed = Math.round(actualOrders * 0.85);
                const cancelled = Math.round(actualOrders * 0.08);

                data.push({
                    date: currentDate.toISOString().split('T')[0],
                    label: currentDate.toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short'
                    }),
                    orders: actualOrders,
                    revenue: actualRevenue,
                    completed: completed,
                    cancelled: cancelled,
                    inProgress: Math.max(0, actualOrders - completed - cancelled)
                });

                remainingOrders -= actualOrders;
                remainingRevenue -= actualRevenue;
            }
        }

        setDateRangeData(data);
    };

    // Generate date trend data for charts
    const generateDateTrendData = useMemo(() => {
        if (isCustomRangeActive && dateRangeData.length > 0) {
            return dateRangeData;
        }

        const getDays = () => {
            switch (timeRange) {
                case 'weekly': return 7;
                case 'monthly': return 30;
                case 'yearly': return 12;
                default: return 30;
            }
        };

        const days = getDays();
        const data = [];
        const baseTotalRevenue = apiStats?.totalRevenue || 0;
        const baseTotalOrders = apiStats?.totalOrders || 0;

        for (let i = days - 1; i >= 0; i--) {
            if (timeRange === 'weekly') {
                data.push({
                    date: `Day ${i + 1}`,
                    label: `Day ${i + 1}`,
                    revenue: Math.round(baseTotalRevenue / 30 * (0.8 + Math.random() * 0.4)),
                    orders: Math.round(baseTotalOrders / 30 * (0.8 + Math.random() * 0.4))
                });
            } else if (timeRange === 'monthly') {
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const monthIndex = (11 - i) % 12;

                data.push({
                    date: months[monthIndex],
                    label: months[monthIndex],
                    revenue: Math.round(baseTotalRevenue / 12 * (0.7 + Math.random() * 0.6)),
                    orders: Math.round(baseTotalOrders / 12 * (0.7 + Math.random() * 0.6))
                });
            } else {
                const years = ['2020', '2021', '2022', '2023', '2024'];
                if (i < years.length) {
                    const yearlyGrowth = 0.8 + (i * 0.1);
                    data.push({
                        date: years[i],
                        label: years[i],
                        revenue: Math.round(baseTotalRevenue * yearlyGrowth),
                        orders: Math.round(baseTotalOrders * yearlyGrowth)
                    });
                }
            }
        }
        return data;
    }, [timeRange, isCustomRangeActive, dateRangeData, apiStats]);

    // Generate order type data
    const generateOrderTypeData = useMemo(() => {
        const types = [
            { name: 'Dine-in', description: 'In-restaurant dining', color: '#7c3aed' },
            { name: 'Takeaway', description: 'Pickup orders', color: '#10b981' },
            { name: 'Delivery', description: 'Home delivery', color: '#3b82f6' },
            { name: 'Catering', description: 'Event catering', color: '#f59e0b' }
        ];

        const totalRevenue = isCustomRangeActive
            ? Number(customTotals?.revenue || 0)
            : Number(apiStats?.totalRevenue || 0);

        return types.map(type => {
            let percentage;
            switch (type.name) {
                case 'Delivery': percentage = 40; break;
                case 'Dine-in': percentage = 35; break;
                case 'Takeaway': percentage = 20; break;
                default: percentage = 5; break;
            }

            const revenue = Math.round(totalRevenue * (percentage / 100));
            const growth = (Math.random() > 0.5 ? 1 : -1) * (2 + Math.random() * 6);

            return {
                ...type,
                revenue,
                percentage,
                growth
            };
        });
    }, [apiStats, customTotals, isCustomRangeActive]);

    // Generate category data
    const generateCategoryData = useMemo(() => {
        const categories = [
            { name: 'Starters', description: 'Appetizers & snacks', color: '#f59e0b' },
            { name: 'Main Course', description: 'Main dishes & curries', color: '#10b981' },
            { name: 'Biryani', description: 'Rice dishes', color: '#ef4444' },
            { name: 'Fast Food', description: 'Burgers, pizzas, etc.', color: '#8b5cf6' },
            { name: 'Desserts', description: 'Sweets & desserts', color: '#ec4899' },
            { name: 'Beverages', description: 'Drinks & beverages', color: '#3b82f6' }
        ];

        const totalRevenue = isCustomRangeActive
            ? Number(customTotals?.revenue || 0)
            : Number(apiStats?.totalRevenue || 0);

        return categories.map(category => {
            let percentage;
            switch (category.name) {
                case 'Main Course': percentage = 35; break;
                case 'Biryani': percentage = 25; break;
                case 'Starters': percentage = 15; break;
                case 'Fast Food': percentage = 10; break;
                case 'Beverages': percentage = 10; break;
                case 'Desserts': percentage = 5; break;
                default: percentage = 0; break;
            }

            const revenue = Math.round(totalRevenue * (percentage / 100));
            const growth = (Math.random() > 0.5 ? 1 : -1) * (3 + Math.random() * 8);

            return {
                ...category,
                revenue,
                percentage,
                growth
            };
        }).sort((a, b) => b.revenue - a.revenue);
    }, [apiStats, customTotals, isCustomRangeActive]);

    // Generate top items data
    const generateTopItemsData = useMemo(() => {
        const items = [
            { name: 'Chicken Biryani', category: 'Biryani', price: 320, cost: 180 },
            { name: 'Paneer Butter Masala', category: 'Main Course', price: 280, cost: 120 },
            { name: 'Veg Spring Rolls', category: 'Starters', price: 180, cost: 80 },
            { name: 'Chocolate Pastry', category: 'Desserts', price: 120, cost: 40 },
            { name: 'Cold Coffee', category: 'Beverages', price: 100, cost: 30 },
            { name: 'Veg Pizza', category: 'Fast Food', price: 200, cost: 100 },
            { name: 'Chicken Curry', category: 'Main Course', price: 300, cost: 140 },
            { name: 'Gulab Jamun', category: 'Desserts', price: 80, cost: 20 },
            { name: 'Mango Shake', category: 'Beverages', price: 110, cost: 35 },
            { name: 'French Fries', category: 'Fast Food', price: 130, cost: 50 }
        ];

        const totalRevenue = isCustomRangeActive
            ? Number(customTotals?.revenue || 0)
            : Number(apiStats?.totalRevenue || 0);

        return items.map(item => {
            const percentage = 2 + Math.random() * 6;
            const revenue = Math.round(totalRevenue * (percentage / 100));
            const orders = Math.round(revenue / item.price);
            const profit = revenue - (orders * item.cost);
            const profitMargin = (profit / revenue) * 100;

            return {
                ...item,
                revenue,
                orders,
                profit,
                profitMargin: parseFloat(profitMargin.toFixed(1)),
                percentage: parseFloat(percentage.toFixed(1))
            };
        }).sort((a, b) => b.revenue - a.revenue);
    }, [apiStats, customTotals, isCustomRangeActive]);

    // Helper functions
    const getDaysDifference = (start, end) => {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const timeDiff = endDate.getTime() - startDate.getTime();
        return Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    // Reset function
    const handleReset = () => {
        showLoader();
        setResetting(true);
        setTimeRange('monthly');
        const defaultStartDate = new Date();
        defaultStartDate.setDate(defaultStartDate.getDate() - 30);
        setStartDate(defaultStartDate.toISOString().split("T")[0]);
        setEndDate(new Date().toISOString().split("T")[0]);
        setAppliedStartDate(null);
        setAppliedEndDate(null);
        setActiveView('revenue');
        setShowDatePicker(false);
        setCustomTotals(null);
        setIsCustomRangeActive(false);
        setApiError(null);
        fetchAdminStatistics();
        setTimeout(() => {
            setResetting(false);
        }, 1000);
    };

    // Handle date range apply
    const handleDateRangeApply = () => {
        showLoader();
        setShowDatePicker(false);
        setTimeRange("custom");
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        fetchCustomRangeTotals(startDate, endDate);
    };

    const handleDateRangeCancel = () => {
        setShowDatePicker(false);
    };

    // Filter handlers
    const handleFilterChange = (data) => {
        setFilteredData(data);
        setIsLoading(false);
    };

    const handleLoadingChange = (loading) => {
        setIsLoading(loading);
    };

    // Get sidebar state from localStorage
    useEffect(() => {
        const checkSidebarState = () => {
            const savedState = localStorage.getItem("sidebarOpen");
            if (savedState !== null) {
                setSidebarOpen(JSON.parse(savedState));
            }
        };

        checkSidebarState();

        const handleStorageChange = (e) => {
            if (e.key === "sidebarOpen") {
                setSidebarOpen(JSON.parse(e.newValue));
            }
        };

        const handleSidebarToggle = (e) => {
            setSidebarOpen(e.detail.isOpen);
        };

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            if (window.innerWidth > 768) {
                setMobileFiltersOpen(false);
            }
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('sidebarToggle', handleSidebarToggle);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('sidebarToggle', handleSidebarToggle);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Filter tabs based on user role
    useEffect(() => {
        const userRole = localStorage.getItem("role");
        const subModules = JSON.parse(localStorage.getItem("subModules") || "[]");

        let availableTabs = [];

        if (userRole === "ROLE_EMPLOYEE") {
            availableTabs = ALL_TABS.filter(tab =>
                subModules.includes(tab.subModuleKey)
            );
        } else {
            availableTabs = [...ALL_TABS];
        }

        setTabs(availableTabs);

        if (availableTabs.length > 0 && activeTab === null) {
            setActiveTab(availableTabs[0].id);
        }
    }, []);

    // Fetch data on component mount
    useEffect(() => {
        const storedVendorId = localStorage.getItem("vendorId");
        if (storedVendorId) {
            setVendorId(storedVendorId);
        } else {
            setVendorId(1);
        }
        fetchAdminStatistics();
    }, []);

    // Refresh custom data when dates change
    useEffect(() => {
        if (timeRange === 'custom' && startDate && endDate) {
            const timer = setTimeout(() => {
                fetchCustomRangeTotals(startDate, endDate);
            }, 500);
            return () => clearTimeout(timer);
        } else {
            setIsCustomRangeActive(false);
        }
    }, [startDate, endDate, timeRange]);

    // Render tab content
    const renderTab = () => {
        const tabProps = {
            data: filteredData,
            isLoading: isLoading,
            isMobile: isMobile,
            colors: colors,
            generateDateTrendData: generateDateTrendData,
            generateOrderTypeData: generateOrderTypeData,
            generateCategoryData: generateCategoryData,
            generateTopItemsData: generateTopItemsData,
            apiStats: apiStats,
            customTotals: customTotals,
            startDate: startDate,
            endDate: endDate,
            isCustomRangeActive: isCustomRangeActive,
            dateRangeData: dateRangeData,
            onFilterChange: handleFilterChange,
            onLoadingChange: handleLoadingChange
        };

        switch (activeTab) {
            case "Financials":
                return <Cateringfinancials {...tabProps} />;
            case "Revenue":
                return <Cateringrevenue {...tabProps} />;
            case "orders":
                return <Cateringsorders {...tabProps} />;
            case "Payments":
                return <Cateringpayments {...tabProps} />;
            default:
                return <Cateringrevenue {...tabProps} />;
        }
    };

    // Time Range Totals Display Component
    const TimeRangeTotalsDisplay = () => {
        const getDisplayData = () => {
            if (isCustomRangeActive && customTotals) {
                return {
                    totalRevenue: customTotals.revenue || 0,
                    totalOrders: customTotals.orders || 0,
                    successRate: customTotals.successRate || "0.00%",
                    totalCustomers: customTotals.customers || 0,
                    completedOrders: customTotals.completedOrders || 0,
                    period: customTotals.period || "Today",
                };
            } else if (apiStats) {
                return {
                    totalRevenue: apiStats.totalRevenue || 0,
                    totalOrders: apiStats.totalOrders || 0,
                    successRate: apiStats.successRate || "0.00%",
                    totalCustomers: apiStats.totalCustomers || 0,
                    completedOrders: apiStats.completedOrders || 0,
                    period: apiStats.period || "Today",
                };
            }
            return null;
        };

        const data = getDisplayData();

        if (!data) {
            return (
                <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                    Loading data...
                </div>
            );
        }

        const formatCurrency = (num) =>
            `₹${Number(num || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            })}`;


    };

    // If no tabs available, show a message
    if (tabs.length === 0) {
        return (
            <div className="food-analysis-dashboard min-h-screen bg-dash">
                <div style={{
                    textAlign: "center",
                    padding: "50px",
                    color: "#64748b"
                }}>
                    <h3>No modules available</h3>
                    <p>You don't have access to any modules in this section.</p>
                </div>
            </div>
        );
    }

    // Dynamic className
    const getDashboardClassName = () => {
        let className = "food-analysis-dashboard min-h-screen bg-dash";
        const isInCustomRange = windowWidth >= 700 && windowWidth <= 1111;
        className += sidebarOpen ? ' sidebar-open' : ' sidebar-closed';
        if (isInCustomRange) {
            className += ' custom-range';
        }
        if (sidebarOpen && isInCustomRange) {
            className += ' sidebar-open-custom-range';
        }
        return className;
    };

    return (
        <div className={getDashboardClassName()}>
            {/* Global Loader */}
            {globalLoading && (
                <div className="global-loader-overlay">
                    <div className="loader-content">
                        <div className="loader-brand">
                            {"MAAMAAS".split("").map((l, i) => (
                                <span key={i} className="loader-letter" style={{ animationDelay: `${i * 0.1}s` }}>
                                    {l}
                                </span>
                            ))}
                        </div>
                        <div className="loader-progress-bar">
                            <div className="loader-progress-fill"></div>
                        </div>
                        <div className="loader-text">Loading reports...</div>
                    </div>
                </div>
            )}

            {/* Header */}



            {/* Time Range Controls */}
            <div className="time-range-controls">


                {/* Date Picker Modal */}
                {showDatePicker && (
                    <div className="date-picker-modal">
                        <div className="date-picker-inputs">
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="date-input"
                            />
                            <span className="date-separator">to</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="date-input"
                            />
                        </div>
                        <div className="date-picker-actions">
                            <button
                                onClick={() => {
                                    const today = new Date();
                                    const todayStr = today.toISOString().split('T')[0];
                                    setStartDate(todayStr);
                                    setEndDate(todayStr);
                                }}
                                className="today-btn"
                            >
                                <Clock size={12} />
                                Today
                            </button>
                            {startDate && endDate && (
                                <div className="days-count">
                                    {getDaysDifference(startDate, endDate)} days
                                </div>
                            )}
                        </div>
                        <div className="date-picker-buttons">
                            <button
                                onClick={() => {
                                    handleDateRangeApply();
                                    setShowDatePicker(false);
                                }}
                                disabled={!startDate || !endDate}
                                className="apply-btn"
                            >
                                Apply
                            </button>
                            <button
                                onClick={() => {
                                    handleDateRangeCancel();
                                    setShowDatePicker(false);
                                }}
                                className="cancel-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Metrics Display */}
            {/* <TimeRangeTotalsDisplay /> */}


            {/* Navigation Tabs */}
            <div className="tabs-navigation">
                <div className="tabs-container">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const active = activeTab === tab.id;

                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`tab-button ${active ? "active" : ""}`}
                            >
                                <Icon size={16} />
                                <span>{tab.label}</span>
                                {active && <span className="tab-indicator"></span>}
                            </button>
                        );
                    })}
                </div>

                <div className="header-right">
                    <button
                        onClick={handleReset}
                        disabled={resetting}
                        className={`reset-btn ${resetting ? "loading" : ""}`}
                    >
                        <RotateCcw size={16} className={resetting ? "spin" : ""} />
                        {resetting ? "..." : "Reset"}
                    </button>

                    <button
                        onClick={() => {
                            const exportData = generateDateTrendData;
                            const ws = XLSX.utils.json_to_sheet(exportData);
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, "Report");
                            XLSX.writeFile(
                                wb,
                                `Food_Report_${new Date().toISOString().split("T")[0]}.xlsx`
                            );
                        }}
                        className="export-btn"
                    >
                        <Download size={16} />
                        Export
                    </button>

                    <button
                        onClick={() => {
                            if (showDatePicker) {
                                setShowDatePicker(false);
                            } else {
                                setTimeRange("custom");
                                setShowDatePicker(true);
                            }
                        }}
                        className={`time-range-btn ${timeRange === "custom" ? "active" : ""
                            }`}
                    >
                        <CalendarIcon size={16} />
                        {showDatePicker
                            ? "Close"
                            : appliedStartDate && appliedEndDate
                                ? `Custom ${formatDate(appliedStartDate)} - ${formatDate(
                                    appliedEndDate
                                )}`
                                : "Custom Range"}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <main className="reports-main-content">
                {renderTab()}
            </main>

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .spin {
                    animation: spin 1s linear infinite;
                }
                
                @keyframes letterAnim {
                    0% { opacity: 0; transform: translateY(30px); }
                    100% { opacity: 1; transform: translateY(0); }
                }

                @keyframes progress {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(250%); }
                }

                .food-analysis-dashboard {
                    // background: #f0f2f5;
                    min-height: 100vh;
                }

                .global-loader-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0,0,0,0.95);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 999999;
                }

                .loader-content {
                    text-align: center;
                }

                .loader-brand {
                    font-size: 2.5rem;
                    font-weight: 900;
                    letter-spacing: 6px;
                    margin-bottom: 30px;
                }

                .loader-letter {
                    display: inline-block;
                    opacity: 0;
                    transform: translateY(30px);
                    animation: letterAnim 0.8s ease forwards;
                    color: #e50914;
                }

                .loader-progress-bar {
                    width: 280px;
                    height: 4px;
                    background: rgba(255,255,255,0.2);
                    overflow: hidden;
                    margin: 0 auto;
                }

                .loader-progress-fill {
                    width: 40%;
                    height: 100%;
                    background: linear-gradient(90deg, #e50914, #ff6b6b);
                    animation: progress 1.8s infinite;
                }

                .loader-text {
                    margin-top: 20px;
                    color: #aaa;
                    font-size: 14px;
                }

                .reports-header {
                    background: white;
                    padding: 16px 24px;
                    border-bottom: 1px solid #e8ecf1;
                }

                .header-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 16px;
                }

                .header-title {
                    font-size: 22px;
                    font-weight: 700;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: #1a1a2e;
                }

                .header-icon {
                    font-size: 24px;
                }

                .header-right {
                    display: flex;
                    gap: 10px;
                }

                .reset-btn, .export-btn {
                    padding: 8px 20px;
                    border: none;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s ease;
                }

                .reset-btn {
                    background: #f59e0b;
                    color: white;
                }

                .reset-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
                }

                .reset-btn.loading {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .export-btn {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                }

                .export-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
                }

                .time-range-controls {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                    align-items: center;
                    margin-bottom: 16px;
                }

                .time-range-selector {
                    display: flex;
                    gap: 8px;
                }

                .time-range-btn {
                    padding: 8px 16px;
                    border: 2px solid rgba(239, 68, 68, 0.2);
                    border-radius: 10px;
                    background: white;
                    color: #ef4444;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    transition: all 0.3s ease;
                    white-space: nowrap;
                }

                .time-range-btn.active {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    color: white;
                    border-color: #ef4444;
                }

                .date-picker-modal {
                    position: absolute;
                    top: calc(100% + 8px);
                    left: 0;
                    background: white;
                    padding: 16px;
                    border-radius: 12px;
                    border: 1px solid #e8ecf1;
                    box-shadow: 0 8px 30px rgba(0,0,0,0.12);
                    z-index: 1001;
                    min-width: 280px;
                }

                .date-picker-inputs {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                    margin-bottom: 12px;
                }

                .date-input {
                    flex: 1;
                    padding: 8px 12px;
                    border: 1px solid #e8ecf1;
                    border-radius: 8px;
                    font-size: 13px;
                    outline: none;
                }

                .date-input:focus {
                    border-color: #667eea;
                }

                .date-separator {
                    color: #94a3b8;
                    font-size: 12px;
                }

                .date-picker-actions {
                    display: flex;
                    gap: 8px;
                    margin-bottom: 12px;
                }

                .today-btn {
                    flex: 1;
                    padding: 6px 12px;
                    background: #f1f5f9;
                    color: #3b82f6;
                    border: 1px solid #e8ecf1;
                    border-radius: 6px;
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                }

                .days-count {
                    flex: 1;
                    padding: 6px 12px;
                    background: #f8fafc;
                    border-radius: 6px;
                    font-size: 11px;
                    color: #64748b;
                    text-align: center;
                }

                .date-picker-buttons {
                    display: flex;
                    gap: 8px;
                }

                .apply-btn {
                    flex: 1;
                    padding: 8px;
                    background: #7c3aed;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                }

                .apply-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .cancel-btn {
                    flex: 1;
                    padding: 8px;
                    background: transparent;
                    color: #64748b;
                    border: 1px solid #e8ecf1;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                }

                .metrics-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 12px;
                }

                .metric-card {
                    padding: 12px 16px;
                    background: #f8fafc;
                    border-radius: 10px;
                    border: 1px solid #e8ecf1;
                }

                .metric-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .metric-value {
                    font-size: 18px;
                    font-weight: 700;
                    color: #1e293b;
                    margin-top: 4px;
                }

                .tabs-navigation {
                    background: white;
                    border-bottom: 1px solid #e8ecf1;
                    padding: 0 24px;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                }

                .tabs-container {
                    display: flex;
                    gap: 0;
                    min-width: max-content;
                }

                .tab-button {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 20px;
                    border: none;
                    background: transparent;
                    color: #64748b;
                    font-weight: 500;
                    font-size: 14px;
                    cursor: pointer;
                    position: relative;
                    transition: all 0.3s ease;
                    white-space: nowrap;
                }

                .tab-button:hover {
                    color: #1e293b;
                }

                .tab-button.active {
                    color: #7c3aed;
                    font-weight: 600;
                }

                .tab-indicator {
                    position: absolute;
                    bottom: 0;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 24px;
                    height: 3px;
                    background: #7c3aed;
                    border-radius: 3px;
                }

                .reports-main-content {
                    padding: 37px 24px;
                    // max-height: calc(100vh - 400px);
                    overflow-y: auto;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .reports-header {
                        padding: 12px 16px;
                    }

                    .header-content {
                        flex-direction: column;
                        gap: 10px;
                    }

                    .header-right {
                        width: 100%;
                    }

                    .reset-btn, .export-btn {
                        flex: 1;
                        justify-content: center;
                    }

                    .time-range-controls {
                        flex-direction: column;
                    }

                    .metrics-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 8px;
                    }

                    .tabs-navigation {
                        padding: 0 16px;
                    }

                    .tab-button {
                        padding: 10px 14px;
                        font-size: 12px;
                    }

                    .reports-main-content {
                        padding: 12px 16px;
                        max-height: calc(100vh - 450px);
                    }

                    .date-picker-modal {
                        position: static;
                        width: 100%;
                        margin-top: 8px;
                    }
                }

                @media (max-width: 480px) {
                    .header-title {
                        font-size: 18px;
                    }

                    .metrics-grid {
                        grid-template-columns: 1fr 1fr;
                        gap: 6px;
                    }

                    .metric-value {
                        font-size: 14px;
                    }

                    .tab-button {
                        padding: 8px 10px;
                        font-size: 11px;
                    }

                    .tab-button svg {
                        width: 14px;
                        height: 14px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Foodreports;